import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  startWith,
  switchMap
} from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

type ApiScalar = string | number | boolean | null | undefined;
type ApiParams = Record<string, ApiScalar>;
type FormMode = 'create' | 'edit';

interface ApiResponse<T = unknown> {
  success?: boolean;
  code?: string | number;
  status?: number;
  message?: string;
  data?: T;
  response?: T;
  pool_point_uuid?: string;
  pool_point_client_uuid?: string;
  pool_point_store_uuid?: string;
  customer_contact_distribution_center_uuid?: string;
  contact_relationship_uuid?: string;
  already_exists?: boolean;
  counts?: {
    clients?: number;
    stores?: number;
  };
  store_count?: number;
}

interface PoolPoint {
  pool_point_uuid: string;
  domain_uuid?: string;
  pool_point_name?: string | null;
  pool_point_contact_uuid?: string | null;
  pool_point_address_uuid?: string | null;
  pool_point_contact_organization?: string | null;
  pool_point_contact_legal_name?: string | null;
  pool_point_contact_email?: string | null;
  address_label?: string | null;
  address_street?: string | null;
  address_extended?: string | null;
  address_locality?: string | null;
  address_region?: string | null;
  address_postal_code?: string | null;
  address_country?: string | null;
  client_count?: string | number | null;
  store_count?: string | number | null;
}

interface PoolPointClient {
  pool_point_client_uuid: string;
  domain_uuid?: string;
  pool_point_uuid?: string | null;
  pool_point_client_name?: string | null;
  pool_point_client_contact_uuid?: string | null;
  pool_point_client_id?: string | null;
  pool_point_client_scope?: string | null;
  pool_point_name?: string | null;
  pool_point_id?: string | null;
  pool_point_kitting_id?: string | null;
  customer_contact_organization?: string | null;
  customer_legal_name?: string | null;
  customer_contact_email?: string | null;
  store_count?: string | number | null;
  distribution_center_count?: string | number | null;
}

interface PoolPointStore {
  pool_point_store_uuid: string;
  domain_uuid?: string;
  pool_point_uuid?: string | null;
  pool_point_store_number?: string | null;
  pool_point_store_contact_uuid?: string | null;
  pool_point_store_address_uuid?: string | null;
  pool_point_client?: string | null;
  pool_point_client_uuid?: string | null;
  pool_point_name?: string | null;
  pool_point_id?: string | null;
  pool_point_client_name?: string | null;
  pool_point_client_contact_uuid?: string | null;
  pool_point_client_id?: string | null;
  pool_point_client_scope?: string | null;
  store_contact_organization?: string | null;
  store_legal_name?: string | null;
  store_contact_email?: string | null;
  address_label?: string | null;
  address_street?: string | null;
  address_extended?: string | null;
  address_locality?: string | null;
  address_region?: string | null;
  address_postal_code?: string | null;
  address_country?: string | null;
  overlapClients?: string[];
}

interface DistributionCenter {
  customer_contact_distribution_center_uuid: string;
  domain_uuid?: string;
  customer_contact_uuid?: string | null;
  distribution_center_contact_uuid?: string | null;
  distribution_center_reference?: string | null;
  customer_contact_organization?: string | null;
  customer_legal_name?: string | null;
  distribution_center_contact_organization?: string | null;
  distribution_center_legal_name?: string | null;
  distribution_center_contact_email?: string | null;
  distribution_center_address_uuid?: string | null;
  distribution_center_address_label?: string | null;
  distribution_center_address_street?: string | null;
  distribution_center_address_extended?: string | null;
  distribution_center_address_locality?: string | null;
  distribution_center_address_region?: string | null;
  distribution_center_address_postal_code?: string | null;
  distribution_center_address_country?: string | null;
}

interface DropTrailerCarrier {
  contact_relationship_uuid: string;
  domain_uuid?: string;
  source_contact_uuid?: string | null;
  contact_uuid?: string | null;
  relationship_type?: string | null;
  created?: string | null;
  contact_organization?: string | null;
  legal_name?: string | null;
  contact_email?: string | null;
  contact_scac?: string | null;
  carrier_mc?: string | null;
  us_dot_no?: string | null;
  active?: string | boolean | null;
}

interface ContactOption {
  contact_uuid: string;
  domain_uuid?: string;
  contact_organization?: string | null;
  legal_name?: string | null;
  contact_email?: string | null;
  contact_scac?: string | null;
  carrier_mc?: string | null;
  us_dot_no?: string | null;
  pool_id?: string | null;
  route_code?: string | null;
  active?: string | boolean | null;
  drop_trailer_party?: string | boolean | null;
  pool_location?: string | boolean | null;
  customer_external_reference?: string | null;
}

interface ContactAddress {
  contact_address_uuid: string;
  domain_uuid?: string;
  contact_uuid?: string;
  address_type?: string | null;
  address_label?: string | null;
  address_primary?: string | number | boolean | null;
  address_street?: string | null;
  address_extended?: string | null;
  address_community?: string | null;
  address_locality?: string | null;
  address_region?: string | null;
  address_postal_code?: string | null;
  address_country?: string | null;
  lat?: string | number | null;
  long?: string | number | null;
  timezone_id?: string | null;
}

interface PoolPointDetail {
  pool_point?: PoolPoint;
  clients?: PoolPointClient[];
  stores?: PoolPointStore[];
}

const API_URL = '/api/pool_manager/pool_manager.php';

@Component({
  selector: 'app-pool-manager',
  templateUrl: './pool-manager.component.html',
  styleUrls: ['./pool-manager.component.scss']
})
export class PoolManagerComponent implements OnInit {
  pools: PoolPoint[] = [];
  clients: PoolPointClient[] = [];
  stores: PoolPointStore[] = [];
  allStoresForPool: PoolPointStore[] = [];
  distributionCenters: DistributionCenter[] = [];
  carriers: DropTrailerCarrier[] = [];

  selectedPool: PoolPoint | null = null;
  selectedClient: PoolPointClient | null = null;
  selectedStore: PoolPointStore | null = null;
  selectedDistributionCenter: DistributionCenter | null = null;
  selectedCarrierContact: ContactOption | null = null;

  poolSearch = '';
  clientSearch = '';
  storeSearch = '';
  dcSearch = '';
  carrierSearch = '';

  poolMode: FormMode = 'edit';
  clientMode: FormMode = 'edit';
  storeMode: FormMode = 'edit';
  distributionCenterMode: FormMode = 'create';

  poolForm: FormGroup;
  clientForm: FormGroup;
  storeForm: FormGroup;
  distributionCenterForm: FormGroup;
  carrierForm: FormGroup;

  poolContactSearch = new FormControl<ContactOption | string>('', { nonNullable: true });
  clientContactSearch = new FormControl<ContactOption | string>('', { nonNullable: true });
  storeContactSearch = new FormControl<ContactOption | string>('', { nonNullable: true });
  customerContactSearch = new FormControl<ContactOption | string>('', { nonNullable: true });
  distributionCenterContactSearch = new FormControl<ContactOption | string>('', { nonNullable: true });
  carrierContactSearch = new FormControl<ContactOption | string>('', { nonNullable: true });

  poolContactOptions$: Observable<ContactOption[]> = of([]);
  clientContactOptions$: Observable<ContactOption[]> = of([]);
  storeContactOptions$: Observable<ContactOption[]> = of([]);
  customerContactOptions$: Observable<ContactOption[]> = of([]);
  distributionCenterContactOptions$: Observable<ContactOption[]> = of([]);
  carrierContactOptions$: Observable<ContactOption[]> = of([]);

  poolAddresses: ContactAddress[] = [];
  storeAddresses: ContactAddress[] = [];

  loadingPools = false;
  loadingDetail = false;
  loadingDistributionCenters = false;
  loadingCarriers = false;
  savingPool = false;
  savingClient = false;
  savingStore = false;
  savingDistributionCenter = false;
  savingCarrier = false;
  loadError = '';

  storesByClient = new Map<string, PoolPointStore[]>();

  constructor(
    private http: HttpClient,
    private fb: FormBuilder,
    private router: Router,
    private toastr: ToastrService
  ) {
    this.poolForm = this.fb.group({
      pool_point_name: ['', Validators.required],
      pool_point_contact_uuid: [''],
      pool_point_address_uuid: ['']
    });

    this.clientForm = this.fb.group({
      pool_point_client_name: ['', Validators.required],
      pool_point_client_id: [''],
      pool_point_client_scope: [''],
      pool_point_client_contact_uuid: ['']
    });

    this.storeForm = this.fb.group({
      pool_point_store_number: ['', Validators.required],
      pool_point_client_uuid: [''],
      pool_point_store_contact_uuid: [''],
      pool_point_store_address_uuid: ['']
    });

    this.distributionCenterForm = this.fb.group({
      customer_contact_uuid: ['', Validators.required],
      distribution_center_contact_uuid: ['', Validators.required],
      distribution_center_reference: ['']
    });

    this.carrierForm = this.fb.group({
      contact_uuid: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.poolContactOptions$ = this.buildContactOptions(this.poolContactSearch);
    this.clientContactOptions$ = this.buildContactOptions(this.clientContactSearch);
    this.storeContactOptions$ = this.buildContactOptions(this.storeContactSearch);
    this.customerContactOptions$ = this.buildContactOptions(this.customerContactSearch);
    this.distributionCenterContactOptions$ = this.buildContactOptions(this.distributionCenterContactSearch);
    this.carrierContactOptions$ = this.buildContactOptions(this.carrierContactSearch);

    this.loadPoolPoints();
    this.loadDistributionCenters();
    this.loadCarriers();
  }

  get filteredPools(): PoolPoint[] {
    const query = this.poolSearch.trim().toLowerCase();
    if (!query) return this.pools;
    return this.pools.filter((pool) => this.poolSearchText(pool).includes(query));
  }

  get filteredClients(): PoolPointClient[] {
    const query = this.clientSearch.trim().toLowerCase();
    if (!query) return this.clients;
    return this.clients.filter((client) => this.clientSearchText(client).includes(query));
  }

  get filteredStores(): PoolPointStore[] {
    const query = this.storeSearch.trim().toLowerCase();
    if (!query) return this.stores;
    return this.stores.filter((store) => this.storeSearchText(store).includes(query));
  }

  get filteredDistributionCenters(): DistributionCenter[] {
    const query = this.dcSearch.trim().toLowerCase();
    if (!query) return this.distributionCenters;
    return this.distributionCenters.filter((dc) => this.distributionCenterSearchText(dc).includes(query));
  }

  get filteredCarriers(): DropTrailerCarrier[] {
    const query = this.carrierSearch.trim().toLowerCase();
    if (!query) return this.carriers;
    return this.carriers.filter((carrier) => this.carrierSearchText(carrier).includes(query));
  }

  get totalStores(): number {
    return this.allStoresForPool.length;
  }

  get overlapCount(): number {
    const counts = new Map<string, number>();
    this.allStoresForPool.forEach((store) => {
      const key = this.storeIdentity(store);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return Array.from(counts.values()).filter((count) => count > 1).length;
  }

  displayContactOption = (contact?: ContactOption | string | null): string => {
    if (!contact) return '';
    return typeof contact === 'string' ? contact : this.contactLabel(contact);
  };

  loadPoolPoints(preferredPoolUuid?: string): void {
    this.loadingPools = true;
    this.loadError = '';
    const currentPoolUuid = preferredPoolUuid || this.selectedPool?.pool_point_uuid || '';

    this.apiGet<PoolPoint[]>('pool_points', { limit: 1000 })
      .pipe(finalize(() => this.loadingPools = false))
      .subscribe({
        next: (res) => {
          if (!this.isSuccess(res)) {
            this.loadError = res.message || 'Pool points could not be loaded.';
            this.clearSelection();
            return;
          }

          this.pools = this.normalizeList<PoolPoint>(res)
            .sort((a, b) => this.poolLabel(a).localeCompare(this.poolLabel(b)));

          const preferredPool = this.pools.find((pool) => pool.pool_point_uuid === currentPoolUuid) || this.pools[0] || null;
          if (preferredPool) {
            this.selectPool(preferredPool);
          } else {
            this.clearSelection();
          }
        },
        error: () => {
          this.loadError = 'Pool points could not be loaded.';
          this.clearSelection();
        }
      });
  }

  selectPool(pool: PoolPoint, preferredClientUuid?: string, preferredStoreUuid?: string): void {
    this.poolMode = 'edit';
    this.selectedPool = { ...pool };
    this.patchPoolForm(this.selectedPool);
    this.resetClientState();
    this.resetStoreState();

    this.loadPoolDetail(pool.pool_point_uuid, preferredClientUuid, preferredStoreUuid);
  }

  startNewPool(): void {
    this.poolMode = 'create';
    this.selectedPool = null;
    this.poolForm.reset({
      pool_point_name: '',
      pool_point_contact_uuid: '',
      pool_point_address_uuid: ''
    });
    this.poolContactSearch.setValue('', { emitEvent: false });
    this.poolAddresses = [];
    this.resetClientState();
    this.resetStoreState();
  }

  savePool(): void {
    if (this.poolForm.invalid) {
      this.poolForm.markAllAsTouched();
      this.toastr.warning('Pool name is required.');
      return;
    }

    const payload: Record<string, unknown> = {
      action: this.poolMode === 'create' ? 'create_pool_point' : 'update_pool_point',
      pool_point_name: this.formValue(this.poolForm, 'pool_point_name'),
      pool_point_contact_uuid: this.formValue(this.poolForm, 'pool_point_contact_uuid'),
      pool_point_address_uuid: this.formValue(this.poolForm, 'pool_point_address_uuid')
    };

    if (this.poolMode === 'edit') {
      if (!this.selectedPool) return;
      payload['pool_point_uuid'] = this.selectedPool.pool_point_uuid;
    }

    this.savingPool = true;
    this.apiPost<PoolPoint>(payload)
      .pipe(finalize(() => this.savingPool = false))
      .subscribe({
        next: (res) => {
          if (!this.isSuccess(res)) {
            this.toastr.error(res.message || 'Pool point was not saved.');
            return;
          }

          const poolUuid = res.pool_point_uuid || this.selectedPool?.pool_point_uuid || this.normalizeItem<PoolPoint>(res)?.pool_point_uuid;
          this.toastr.success(this.poolMode === 'create' ? 'Pool point created.' : 'Pool point saved.');
          this.poolMode = 'edit';
          this.loadPoolPoints(poolUuid);
        },
        error: () => this.toastr.error('Pool point was not saved.')
      });
  }

  deletePool(): void {
    if (!this.selectedPool) return;

    const childCount = Math.max(
      this.toNumber(this.selectedPool.client_count) + this.toNumber(this.selectedPool.store_count),
      this.clients.length + this.allStoresForPool.length
    );
    const message = childCount > 0
      ? `Delete ${this.poolLabel(this.selectedPool)} and its linked clients/stores?`
      : `Delete ${this.poolLabel(this.selectedPool)}?`;

    if (!window.confirm(message)) return;

    this.apiPost<unknown>({
      action: 'delete_pool_point',
      pool_point_uuid: this.selectedPool.pool_point_uuid,
      force: childCount > 0
    }).subscribe({
      next: (res) => {
        if (!this.isSuccess(res)) {
          this.toastr.error(res.message || 'Pool point was not deleted.');
          return;
        }
        this.toastr.success('Pool point deleted.');
        this.loadPoolPoints();
      },
      error: () => this.toastr.error('Pool point was not deleted.')
    });
  }

  selectClient(client: PoolPointClient | null, preferredStoreUuid?: string): void {
    if (!client) {
      this.selectedClient = null;
      this.clientMode = 'create';
      this.clientForm.reset({
        pool_point_client_name: '',
        pool_point_client_id: '',
        pool_point_client_scope: '',
        pool_point_client_contact_uuid: ''
      });
      this.clientContactSearch.setValue('', { emitEvent: false });
      this.stores = [];
      this.selectStore(null);
      return;
    }

    this.clientMode = 'edit';
    this.selectedClient = { ...client };
    this.patchClientForm(this.selectedClient);
    this.stores = this.storesByClient.get(client.pool_point_client_uuid) || [];

    const preferredStore = preferredStoreUuid
      ? this.stores.find((store) => store.pool_point_store_uuid === preferredStoreUuid)
      : null;
    this.selectStore(preferredStore || this.stores[0] || null);
  }

  startNewClient(): void {
    if (!this.selectedPool) return;
    this.clientMode = 'create';
    this.selectedClient = null;
    this.clientForm.reset({
      pool_point_client_name: '',
      pool_point_client_id: '',
      pool_point_client_scope: '',
      pool_point_client_contact_uuid: ''
    });
    this.clientContactSearch.setValue('', { emitEvent: false });
    this.selectedStore = null;
    this.stores = [];
    this.storeMode = 'create';
    this.storeForm.reset({
      pool_point_store_number: '',
      pool_point_client_uuid: '',
      pool_point_store_contact_uuid: '',
      pool_point_store_address_uuid: ''
    });
    this.storeContactSearch.setValue('', { emitEvent: false });
    this.storeAddresses = [];
  }

  saveClient(): void {
    if (!this.selectedPool) return;
    if (this.clientForm.invalid) {
      this.clientForm.markAllAsTouched();
      this.toastr.warning('Client name is required.');
      return;
    }

    const payload: Record<string, unknown> = {
      action: this.clientMode === 'create' ? 'create_pool_point_client' : 'update_pool_point_client',
      pool_point_uuid: this.selectedPool.pool_point_uuid,
      pool_point_client_name: this.formValue(this.clientForm, 'pool_point_client_name'),
      pool_point_client_contact_uuid: this.formValue(this.clientForm, 'pool_point_client_contact_uuid'),
      pool_point_client_id: this.formValue(this.clientForm, 'pool_point_client_id'),
      pool_point_client_scope: this.formValue(this.clientForm, 'pool_point_client_scope')
    };

    if (this.clientMode === 'edit') {
      if (!this.selectedClient) return;
      payload['pool_point_client_uuid'] = this.selectedClient.pool_point_client_uuid;
    }

    this.savingClient = true;
    this.apiPost<PoolPointClient>(payload)
      .pipe(finalize(() => this.savingClient = false))
      .subscribe({
        next: (res) => {
          if (!this.isSuccess(res)) {
            this.toastr.error(res.message || 'Client was not saved.');
            return;
          }

          const clientUuid = res.pool_point_client_uuid
            || this.selectedClient?.pool_point_client_uuid
            || this.normalizeItem<PoolPointClient>(res)?.pool_point_client_uuid;
          this.toastr.success(this.clientMode === 'create' ? 'Client added.' : 'Client saved.');
          this.loadPoolDetail(this.selectedPool!.pool_point_uuid, clientUuid);
        },
        error: () => this.toastr.error('Client was not saved.')
      });
  }

  deleteClient(): void {
    if (!this.selectedPool || !this.selectedClient) return;

    const storeCount = this.toNumber(this.selectedClient.store_count) || this.stores.length;
    const message = storeCount > 0
      ? `Delete ${this.clientLabel(this.selectedClient)} and its linked stores?`
      : `Delete ${this.clientLabel(this.selectedClient)}?`;

    if (!window.confirm(message)) return;

    this.apiPost<unknown>({
      action: 'delete_pool_point_client',
      pool_point_client_uuid: this.selectedClient.pool_point_client_uuid,
      force: storeCount > 0
    }).subscribe({
      next: (res) => {
        if (!this.isSuccess(res)) {
          this.toastr.error(res.message || 'Client was not deleted.');
          return;
        }
        this.toastr.success('Client deleted.');
        this.loadPoolDetail(this.selectedPool!.pool_point_uuid);
      },
      error: () => this.toastr.error('Client was not deleted.')
    });
  }

  selectStore(store: PoolPointStore | null): void {
    this.storeMode = store ? 'edit' : 'create';
    this.selectedStore = store ? { ...store } : null;

    if (store) {
      this.patchStoreForm(store);
    } else {
      this.storeForm.reset({
        pool_point_store_number: '',
        pool_point_client_uuid: this.selectedClient?.pool_point_client_uuid || '',
        pool_point_store_contact_uuid: '',
        pool_point_store_address_uuid: ''
      });
      this.storeContactSearch.setValue('', { emitEvent: false });
      this.storeAddresses = [];
    }
  }

  startNewStore(): void {
    if (!this.selectedClient) return;
    this.storeMode = 'create';
    this.selectStore(null);
  }

  saveStore(): void {
    if (!this.selectedPool) return;
    if (this.storeForm.invalid) {
      this.storeForm.markAllAsTouched();
      this.toastr.warning('Store number is required.');
      return;
    }

    const selectedClientUuid = this.formValue(this.storeForm, 'pool_point_client_uuid')
      || this.selectedClient?.pool_point_client_uuid
      || null;

    const payload: Record<string, unknown> = {
      action: this.storeMode === 'create' ? 'create_pool_point_store' : 'update_pool_point_store',
      pool_point_uuid: this.selectedPool.pool_point_uuid,
      pool_point_client_uuid: selectedClientUuid,
      pool_point_store_number: this.formValue(this.storeForm, 'pool_point_store_number'),
      pool_point_store_contact_uuid: this.formValue(this.storeForm, 'pool_point_store_contact_uuid'),
      pool_point_store_address_uuid: this.formValue(this.storeForm, 'pool_point_store_address_uuid')
    };

    if (this.storeMode === 'edit') {
      if (!this.selectedStore) return;
      payload['pool_point_store_uuid'] = this.selectedStore.pool_point_store_uuid;
    }

    this.savingStore = true;
    this.apiPost<PoolPointStore>(payload)
      .pipe(finalize(() => this.savingStore = false))
      .subscribe({
        next: (res) => {
          if (!this.isSuccess(res)) {
            this.toastr.error(res.message || 'Store was not saved.');
            return;
          }

          const storeUuid = res.pool_point_store_uuid
            || this.selectedStore?.pool_point_store_uuid
            || this.normalizeItem<PoolPointStore>(res)?.pool_point_store_uuid;
          const clientUuid = selectedClientUuid || this.selectedClient?.pool_point_client_uuid || undefined;
          this.toastr.success(this.storeMode === 'create' ? 'Store added.' : 'Store saved.');
          this.loadPoolDetail(this.selectedPool!.pool_point_uuid, clientUuid, storeUuid);
        },
        error: () => this.toastr.error('Store was not saved.')
      });
  }

  deleteStore(store: PoolPointStore, event?: Event): void {
    event?.stopPropagation();
    if (!this.selectedPool || !window.confirm(`Delete ${this.storeLabel(store)}?`)) return;

    this.apiPost<unknown>({
      action: 'delete_pool_point_store',
      pool_point_store_uuid: store.pool_point_store_uuid
    }).subscribe({
      next: (res) => {
        if (!this.isSuccess(res)) {
          this.toastr.error(res.message || 'Store was not deleted.');
          return;
        }
        this.toastr.success('Store deleted.');
        this.loadPoolDetail(this.selectedPool!.pool_point_uuid, this.selectedClient?.pool_point_client_uuid);
      },
      error: () => this.toastr.error('Store was not deleted.')
    });
  }

  selectDistributionCenter(dc: DistributionCenter): void {
    this.distributionCenterMode = 'edit';
    this.selectedDistributionCenter = { ...dc };
    this.distributionCenterForm.reset({
      customer_contact_uuid: dc.customer_contact_uuid || '',
      distribution_center_contact_uuid: dc.distribution_center_contact_uuid || '',
      distribution_center_reference: dc.distribution_center_reference || ''
    });
    this.customerContactSearch.setValue(this.customerLabel(dc), { emitEvent: false });
    this.distributionCenterContactSearch.setValue(this.distributionCenterLabel(dc), { emitEvent: false });
  }

  startNewDistributionCenter(): void {
    this.distributionCenterMode = 'create';
    this.selectedDistributionCenter = null;
    this.distributionCenterForm.reset({
      customer_contact_uuid: '',
      distribution_center_contact_uuid: '',
      distribution_center_reference: ''
    });
    this.customerContactSearch.setValue('', { emitEvent: false });
    this.distributionCenterContactSearch.setValue('', { emitEvent: false });
  }

  saveDistributionCenter(): void {
    if (this.distributionCenterForm.invalid) {
      this.distributionCenterForm.markAllAsTouched();
      this.toastr.warning('Customer and distribution center contacts are required.');
      return;
    }

    const payload: Record<string, unknown> = {
      action: this.distributionCenterMode === 'create' ? 'create_distribution_center' : 'update_distribution_center',
      customer_contact_uuid: this.formValue(this.distributionCenterForm, 'customer_contact_uuid'),
      distribution_center_contact_uuid: this.formValue(this.distributionCenterForm, 'distribution_center_contact_uuid'),
      distribution_center_reference: this.formValue(this.distributionCenterForm, 'distribution_center_reference')
    };

    if (this.distributionCenterMode === 'edit') {
      if (!this.selectedDistributionCenter) return;
      payload['customer_contact_distribution_center_uuid'] = this.selectedDistributionCenter.customer_contact_distribution_center_uuid;
    }

    this.savingDistributionCenter = true;
    this.apiPost<DistributionCenter>(payload)
      .pipe(finalize(() => this.savingDistributionCenter = false))
      .subscribe({
        next: (res) => {
          if (!this.isSuccess(res)) {
            this.toastr.error(res.message || 'Distribution center was not saved.');
            return;
          }

          const dcUuid = res.customer_contact_distribution_center_uuid
            || this.selectedDistributionCenter?.customer_contact_distribution_center_uuid
            || this.normalizeItem<DistributionCenter>(res)?.customer_contact_distribution_center_uuid;
          this.toastr.success(this.distributionCenterMode === 'create' ? 'Distribution center added.' : 'Distribution center saved.');
          this.loadDistributionCenters(dcUuid);
        },
        error: () => this.toastr.error('Distribution center was not saved.')
      });
  }

  deleteDistributionCenter(dc: DistributionCenter, event?: Event): void {
    event?.stopPropagation();
    if (!window.confirm(`Remove ${this.distributionCenterLabel(dc)}?`)) return;

    this.apiPost<unknown>({
      action: 'delete_distribution_center',
      customer_contact_distribution_center_uuid: dc.customer_contact_distribution_center_uuid
    }).subscribe({
      next: (res) => {
        if (!this.isSuccess(res)) {
          this.toastr.error(res.message || 'Distribution center was not removed.');
          return;
        }
        this.toastr.success('Distribution center removed.');
        this.loadDistributionCenters();
      },
      error: () => this.toastr.error('Distribution center was not removed.')
    });
  }

  loadCarriers(): void {
    this.loadingCarriers = true;
    this.apiGet<DropTrailerCarrier[]>('drop_trailer_carriers', { limit: 1000 })
      .pipe(finalize(() => this.loadingCarriers = false))
      .subscribe({
        next: (res) => {
          if (!this.isSuccess(res)) {
            this.toastr.error(res.message || 'Drop trailer carriers could not be loaded.');
            this.carriers = [];
            return;
          }
          this.carriers = this.normalizeList<DropTrailerCarrier>(res)
            .sort((a, b) => this.carrierLabel(a).localeCompare(this.carrierLabel(b)));
        },
        error: () => {
          this.carriers = [];
          this.toastr.error('Drop trailer carriers could not be loaded.');
        }
      });
  }

  addCarrier(): void {
    if (this.carrierForm.invalid) {
      this.carrierForm.markAllAsTouched();
      this.toastr.warning('Select a carrier contact.');
      return;
    }

    this.savingCarrier = true;
    this.apiPost<DropTrailerCarrier>({
      action: 'add_drop_trailer_carrier',
      contact_uuid: this.formValue(this.carrierForm, 'contact_uuid')
    })
      .pipe(finalize(() => this.savingCarrier = false))
      .subscribe({
        next: (res) => {
          if (!this.isSuccess(res)) {
            this.toastr.error(res.message || 'Carrier was not added.');
            return;
          }
          this.toastr.success(res.already_exists ? 'Carrier already listed.' : 'Drop trailer carrier added.');
          this.carrierForm.reset({ contact_uuid: '' });
          this.carrierContactSearch.setValue('', { emitEvent: false });
          this.selectedCarrierContact = null;
          this.loadCarriers();
        },
        error: () => this.toastr.error('Carrier was not added.')
      });
  }

  removeCarrier(carrier: DropTrailerCarrier, event?: Event): void {
    event?.stopPropagation();
    if (!window.confirm(`Remove ${this.carrierLabel(carrier)} from drop trailer carriers?`)) return;

    this.apiPost<unknown>({
      action: 'remove_drop_trailer_carrier',
      contact_relationship_uuid: carrier.contact_relationship_uuid
    }).subscribe({
      next: (res) => {
        if (!this.isSuccess(res)) {
          this.toastr.error(res.message || 'Carrier was not removed.');
          return;
        }
        this.toastr.success('Drop trailer carrier removed.');
        this.loadCarriers();
      },
      error: () => this.toastr.error('Carrier was not removed.')
    });
  }

  onPoolContactSelected(contact: ContactOption): void {
    this.poolForm.patchValue({
      pool_point_contact_uuid: contact.contact_uuid,
      pool_point_address_uuid: ''
    });
    this.poolContactSearch.setValue(contact, { emitEvent: false });
    this.loadContactAddresses(contact.contact_uuid, 'pool');
  }

  onClientContactSelected(contact: ContactOption): void {
    this.clientForm.patchValue({
      pool_point_client_contact_uuid: contact.contact_uuid
    });
    this.clientContactSearch.setValue(contact, { emitEvent: false });
  }

  onStoreContactSelected(contact: ContactOption): void {
    this.storeForm.patchValue({
      pool_point_store_contact_uuid: contact.contact_uuid,
      pool_point_store_address_uuid: ''
    });
    this.storeContactSearch.setValue(contact, { emitEvent: false });
    if (!this.formValue(this.storeForm, 'pool_point_store_number')) {
      this.storeForm.patchValue({
        pool_point_store_number: contact.route_code || contact.customer_external_reference || contact.pool_id || ''
      });
    }
    this.loadContactAddresses(contact.contact_uuid, 'store');
  }

  onCustomerContactSelected(contact: ContactOption): void {
    this.distributionCenterForm.patchValue({
      customer_contact_uuid: contact.contact_uuid
    });
    this.customerContactSearch.setValue(contact, { emitEvent: false });
  }

  onDistributionCenterContactSelected(contact: ContactOption): void {
    this.distributionCenterForm.patchValue({
      distribution_center_contact_uuid: contact.contact_uuid
    });
    this.distributionCenterContactSearch.setValue(contact, { emitEvent: false });
  }

  onCarrierContactSelected(contact: ContactOption): void {
    this.selectedCarrierContact = contact;
    this.carrierForm.patchValue({
      contact_uuid: contact.contact_uuid
    });
    this.carrierContactSearch.setValue(contact, { emitEvent: false });
  }

  openContact(contactUuid?: string | null, event?: Event): void {
    event?.stopPropagation();
    if (!contactUuid) {
      this.toastr.warning('No contact is attached.');
      return;
    }
    this.router.navigate(['/contacts'], { queryParams: { contact_uuid: contactUuid } });
  }

  refresh(): void {
    this.loadPoolPoints(this.selectedPool?.pool_point_uuid);
    this.loadDistributionCenters(this.selectedDistributionCenter?.customer_contact_distribution_center_uuid);
    this.loadCarriers();
  }

  poolLabel(pool: PoolPoint | null): string {
    if (!pool) return 'New pool point';
    return pool.pool_point_name
      || pool.pool_point_contact_organization
      || pool.pool_point_contact_legal_name
      || 'Unnamed pool point';
  }

  clientLabel(client: PoolPointClient | null): string {
    if (!client) return 'No client selected';
    return client.pool_point_client_name
      || client.customer_contact_organization
      || client.customer_legal_name
      || client.pool_point_client_id
      || 'Unnamed client';
  }

  storeLabel(store: PoolPointStore | null): string {
    if (!store) return 'New store';
    const number = store.pool_point_store_number ? `#${store.pool_point_store_number}` : '';
    return [
      number,
      store.store_contact_organization || store.store_legal_name
    ].filter(Boolean).join(' - ') || 'Unnamed store';
  }

  distributionCenterLabel(dc: DistributionCenter | null): string {
    if (!dc) return 'New distribution center';
    return dc.distribution_center_contact_organization
      || dc.distribution_center_legal_name
      || dc.distribution_center_reference
      || 'Unnamed distribution center';
  }

  customerLabel(dc: DistributionCenter | null): string {
    if (!dc) return 'No customer';
    return dc.customer_contact_organization
      || dc.customer_legal_name
      || dc.customer_contact_uuid
      || 'Unnamed customer';
  }

  carrierLabel(carrier: DropTrailerCarrier | null): string {
    if (!carrier) return 'No carrier selected';
    return carrier.contact_organization || carrier.legal_name || carrier.contact_scac || 'Unnamed carrier';
  }

  contactLabel(contact: ContactOption | null): string {
    if (!contact) return '';
    return contact.contact_organization
      || contact.legal_name
      || contact.contact_email
      || contact.contact_scac
      || contact.contact_uuid
      || 'Contact';
  }

  contactMeta(contact: ContactOption): string {
    return [
      contact.contact_scac ? `SCAC ${contact.contact_scac}` : '',
      contact.carrier_mc ? `MC ${contact.carrier_mc}` : '',
      contact.us_dot_no ? `DOT ${contact.us_dot_no}` : '',
      contact.pool_id ? `Pool ${contact.pool_id}` : '',
      contact.customer_external_reference || ''
    ].filter(Boolean).join(' | ');
  }

  addressLabel(address: ContactAddress | null): string {
    if (!address) return 'No address';
    const label = address.address_label ? `${address.address_label}: ` : '';
    return `${label}${this.addressParts([
      address.address_street,
      address.address_locality,
      address.address_region,
      address.address_postal_code,
      address.address_country
    ])}`;
  }

  addressForPool(pool: PoolPoint | null): string {
    if (!pool) return 'No address on file';
    return this.addressParts([
      pool.address_street,
      pool.address_locality,
      pool.address_region,
      pool.address_postal_code,
      pool.address_country
    ]);
  }

  addressForStore(store: PoolPointStore | null): string {
    if (!store) return 'No address on file';
    return this.addressParts([
      store.address_street,
      store.address_locality,
      store.address_region,
      store.address_postal_code,
      store.address_country
    ]);
  }

  addressForDistributionCenter(dc: DistributionCenter | null): string {
    if (!dc) return 'No address on file';
    return this.addressParts([
      dc.distribution_center_address_street,
      dc.distribution_center_address_locality,
      dc.distribution_center_address_region,
      dc.distribution_center_address_postal_code,
      dc.distribution_center_address_country
    ]);
  }

  carrierMeta(carrier: DropTrailerCarrier): string {
    return [
      carrier.contact_scac ? `SCAC ${carrier.contact_scac}` : '',
      carrier.carrier_mc ? `MC ${carrier.carrier_mc}` : '',
      carrier.us_dot_no ? `DOT ${carrier.us_dot_no}` : '',
      carrier.active === false || carrier.active === 'f' ? 'Inactive' : ''
    ].filter(Boolean).join(' | ');
  }

  count(value: string | number | null | undefined): number {
    return this.toNumber(value);
  }

  trackPool(_: number, pool: PoolPoint): string {
    return pool.pool_point_uuid;
  }

  trackClient(_: number, client: PoolPointClient): string {
    return client.pool_point_client_uuid;
  }

  trackStore(index: number, store: PoolPointStore): string {
    return store.pool_point_store_uuid || `${store.pool_point_store_contact_uuid || 'store'}-${index}`;
  }

  trackDistributionCenter(_: number, dc: DistributionCenter): string {
    return dc.customer_contact_distribution_center_uuid;
  }

  trackCarrier(_: number, carrier: DropTrailerCarrier): string {
    return carrier.contact_relationship_uuid;
  }

  trackAddress(_: number, address: ContactAddress): string {
    return address.contact_address_uuid;
  }

  private loadPoolDetail(poolPointUuid: string, preferredClientUuid?: string, preferredStoreUuid?: string): void {
    this.loadingDetail = true;

    this.apiGet<PoolPointDetail>('pool_point', { pool_point_uuid: poolPointUuid })
      .pipe(finalize(() => this.loadingDetail = false))
      .subscribe({
        next: (res) => {
          if (!this.isSuccess(res) || !res.data) {
            this.toastr.error(res.message || 'Pool point details could not be loaded.');
            return;
          }

          const detail = res.data;
          if (detail.pool_point) {
            this.selectedPool = detail.pool_point;
            this.patchPoolForm(detail.pool_point);
          }

          this.clients = this.normalizeList<PoolPointClient>(detail.clients || [])
            .sort((a, b) => this.clientLabel(a).localeCompare(this.clientLabel(b)));
          this.allStoresForPool = this.normalizeList<PoolPointStore>(detail.stores || [])
            .sort((a, b) => this.storeLabel(a).localeCompare(this.storeLabel(b), undefined, { numeric: true }));

          this.rebuildStoresByClient();
          this.markOverlaps();

          const nextClient = preferredClientUuid
            ? this.clients.find((client) => client.pool_point_client_uuid === preferredClientUuid)
            : this.clients.find((client) => client.pool_point_client_uuid === this.selectedClient?.pool_point_client_uuid);
          this.selectClient(nextClient || this.clients[0] || null, preferredStoreUuid);
        },
        error: () => this.toastr.error('Pool point details could not be loaded.')
      });
  }

  loadDistributionCenters(preferredDcUuid?: string): void {
    this.loadingDistributionCenters = true;
    this.apiGet<DistributionCenter[]>('distribution_centers', {
      limit: 1000
    })
      .pipe(finalize(() => this.loadingDistributionCenters = false))
      .subscribe({
        next: (res) => {
          if (!this.isSuccess(res)) {
            this.toastr.error(res.message || 'Distribution centers could not be loaded.');
            this.distributionCenters = [];
            return;
          }

          this.distributionCenters = this.normalizeList<DistributionCenter>(res)
            .sort((a, b) => this.distributionCenterLabel(a).localeCompare(this.distributionCenterLabel(b)));

          const selected = preferredDcUuid
            ? this.distributionCenters.find((dc) => dc.customer_contact_distribution_center_uuid === preferredDcUuid)
            : this.distributionCenters[0];
          selected ? this.selectDistributionCenter(selected) : this.startNewDistributionCenter();
        },
        error: () => {
          this.distributionCenters = [];
          this.toastr.error('Distribution centers could not be loaded.');
        }
      });
  }

  private loadContactAddresses(contactUuid: string, target: 'pool' | 'store'): void {
    this.apiGet<ContactAddress[]>('contact_addresses', { contact_uuid: contactUuid })
      .pipe(catchError(() => of({ success: false, data: [] as ContactAddress[] } as ApiResponse<ContactAddress[]>)))
      .subscribe((res) => {
        const addresses = this.isSuccess(res) ? this.normalizeList<ContactAddress>(res) : [];
        if (target === 'pool') {
          this.poolAddresses = addresses;
        } else {
          this.storeAddresses = addresses;
        }
      });
  }

  private buildContactOptions(control: FormControl<ContactOption | string>, extraParams: ApiParams = {}): Observable<ContactOption[]> {
    return control.valueChanges.pipe(
      startWith(control.value),
      debounceTime(250),
      distinctUntilChanged(),
      switchMap((value) => {
        const query = typeof value === 'string' ? value.trim() : this.contactLabel(value).trim();
        if (query.length < 2) return of([]);
        return this.searchContacts(query, extraParams);
      })
    );
  }

  private searchContacts(search: string, extraParams: ApiParams): Observable<ContactOption[]> {
    return this.apiGet<ContactOption[]>('contacts', {
      search,
      limit: 40,
      ...extraParams
    }).pipe(
      map((res) => this.isSuccess(res) ? this.normalizeList<ContactOption>(res) : []),
      catchError(() => of([]))
    );
  }

  private patchPoolForm(pool: PoolPoint): void {
    this.poolForm.reset({
      pool_point_name: pool.pool_point_name || '',
      pool_point_contact_uuid: pool.pool_point_contact_uuid || '',
      pool_point_address_uuid: pool.pool_point_address_uuid || ''
    });
    this.poolContactSearch.setValue(
      pool.pool_point_contact_organization || pool.pool_point_contact_legal_name || pool.pool_point_contact_uuid || '',
      { emitEvent: false }
    );
    if (pool.pool_point_contact_uuid) {
      this.loadContactAddresses(pool.pool_point_contact_uuid, 'pool');
    } else {
      this.poolAddresses = [];
    }
  }

  private patchClientForm(client: PoolPointClient): void {
    this.clientForm.reset({
      pool_point_client_name: client.pool_point_client_name || '',
      pool_point_client_id: client.pool_point_client_id || '',
      pool_point_client_scope: client.pool_point_client_scope || '',
      pool_point_client_contact_uuid: client.pool_point_client_contact_uuid || ''
    });
    this.clientContactSearch.setValue(
      client.customer_contact_organization || client.customer_legal_name || client.pool_point_client_contact_uuid || '',
      { emitEvent: false }
    );
  }

  private patchStoreForm(store: PoolPointStore): void {
    this.storeForm.reset({
      pool_point_store_number: store.pool_point_store_number || '',
      pool_point_client_uuid: store.pool_point_client_uuid || this.selectedClient?.pool_point_client_uuid || '',
      pool_point_store_contact_uuid: store.pool_point_store_contact_uuid || '',
      pool_point_store_address_uuid: store.pool_point_store_address_uuid || ''
    });
    this.storeContactSearch.setValue(
      store.store_contact_organization || store.store_legal_name || store.pool_point_store_contact_uuid || '',
      { emitEvent: false }
    );
    if (store.pool_point_store_contact_uuid) {
      this.loadContactAddresses(store.pool_point_store_contact_uuid, 'store');
    } else {
      this.storeAddresses = [];
    }
  }

  private rebuildStoresByClient(): void {
    this.storesByClient.clear();
    this.clients.forEach((client) => {
      const stores = this.allStoresForPool.filter((store) => store.pool_point_client_uuid === client.pool_point_client_uuid);
      this.storesByClient.set(client.pool_point_client_uuid, stores);
    });
  }

  private markOverlaps(): void {
    const clientsByStore = new Map<string, Set<string>>();

    this.allStoresForPool.forEach((store) => {
      const key = this.storeIdentity(store);
      const clients = clientsByStore.get(key) || new Set<string>();
      clients.add(store.pool_point_client || store.pool_point_client_name || 'Unknown client');
      clientsByStore.set(key, clients);
    });

    this.allStoresForPool.forEach((store) => {
      const allClients = Array.from(clientsByStore.get(this.storeIdentity(store)) || []);
      const currentClient = store.pool_point_client || store.pool_point_client_name || 'Unknown client';
      store.overlapClients = allClients.filter((client) => client !== currentClient);
    });
  }

  private storeIdentity(store: PoolPointStore): string {
    return store.pool_point_store_contact_uuid
      || `${store.pool_point_store_number || ''}:${store.store_contact_organization || store.store_legal_name || ''}`.toLowerCase();
  }

  private apiGet<T>(requestType: string, params: ApiParams = {}): Observable<ApiResponse<T>> {
    let httpParams = new HttpParams().set('request_type', requestType);
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });

    return this.http.get<ApiResponse<T>>(API_URL, { params: httpParams });
  }

  private apiPost<T>(body: Record<string, unknown>): Observable<ApiResponse<T>> {
    return this.http.post<ApiResponse<T>>(API_URL, body);
  }

  private normalizeList<T>(res: ApiResponse<T[]> | T[] | undefined): T[] {
    if (!res) return [];
    if (Array.isArray(res)) return res.filter(Boolean);
    if (Array.isArray(res.data)) return res.data.filter(Boolean);
    if (Array.isArray(res.response)) return res.response.filter(Boolean);
    return [];
  }

  private normalizeItem<T>(res: ApiResponse<T> | undefined): T | null {
    if (!res) return null;
    return res.data || null;
  }

  private isSuccess(res: ApiResponse<unknown> | undefined): boolean {
    if (!res) return false;
    return res.success === true || res.code === 200 || res.code === '200' || res.status === 200;
  }

  private formValue(form: FormGroup, key: string): string | null {
    const value = form.get(key)?.value as unknown;
    if (value === null || value === undefined) return null;
    const text = String(value).trim();
    return text === '' ? null : text;
  }

  private addressParts(parts: Array<string | null | undefined>): string {
    return parts.filter(Boolean).join(', ') || 'No address on file';
  }

  private toNumber(value: string | number | null | undefined): number {
    const numberValue = Number(value || 0);
    return Number.isFinite(numberValue) ? numberValue : 0;
  }

  private poolSearchText(pool: PoolPoint): string {
    return [
      this.poolLabel(pool),
      pool.pool_point_contact_email,
      pool.address_locality,
      pool.address_region,
      pool.address_postal_code
    ].filter(Boolean).join(' ').toLowerCase();
  }

  private clientSearchText(client: PoolPointClient): string {
    return [
      this.clientLabel(client),
      client.pool_point_client_id,
      client.pool_point_client_scope,
      client.customer_contact_email,
      client.pool_point_client_contact_uuid
    ].filter(Boolean).join(' ').toLowerCase();
  }

  private storeSearchText(store: PoolPointStore): string {
    return [
      this.storeLabel(store),
      store.pool_point_store_number,
      store.address_street,
      store.address_locality,
      store.address_region,
      store.address_postal_code
    ].filter(Boolean).join(' ').toLowerCase();
  }

  private distributionCenterSearchText(dc: DistributionCenter): string {
    return [
      this.distributionCenterLabel(dc),
      dc.distribution_center_reference,
      dc.distribution_center_address_locality,
      dc.distribution_center_address_region,
      dc.distribution_center_address_postal_code
    ].filter(Boolean).join(' ').toLowerCase();
  }

  private carrierSearchText(carrier: DropTrailerCarrier): string {
    return [
      this.carrierLabel(carrier),
      carrier.contact_scac,
      carrier.carrier_mc,
      carrier.us_dot_no,
      carrier.contact_email
    ].filter(Boolean).join(' ').toLowerCase();
  }

  private resetClientState(): void {
    this.selectedClient = null;
    this.clients = [];
    this.clientSearch = '';
    this.clientMode = 'edit';
    this.clientForm.reset({
      pool_point_client_name: '',
      pool_point_client_id: '',
      pool_point_client_scope: '',
      pool_point_client_contact_uuid: ''
    });
    this.clientContactSearch.setValue('', { emitEvent: false });
  }

  private resetStoreState(): void {
    this.selectedStore = null;
    this.stores = [];
    this.allStoresForPool = [];
    this.storeSearch = '';
    this.storeMode = 'create';
    this.storeForm.reset({
      pool_point_store_number: '',
      pool_point_client_uuid: '',
      pool_point_store_contact_uuid: '',
      pool_point_store_address_uuid: ''
    });
    this.storeContactSearch.setValue('', { emitEvent: false });
    this.storeAddresses = [];
    this.storesByClient.clear();
  }

  private clearSelection(): void {
    this.selectedPool = null;
    this.poolMode = 'create';
    this.startNewPool();
  }
}
